// Раздел «Записи» листа управления песней (волна F, задача 8).
//
// Что это. У песни бывает три записи, и свежая не значит удачная. Этот раздел
// отвечает владельцу на три вопроса про НИХ: какая открывается первой, кто её
// слышит, и как убрать ту, которую слушать больше не надо. Форма взята у
// прототипа (docs/prototypes/2026-09-18-online-library.html, `buildOwnerPanel`,
// секция «Which recording opens first»): строка на запись, кружок выбора
// слева, имя над метастрокой, метка «Default» справа.
//
// Модуль ЧИСТЫЙ, как song-sheet-words.ts рядом: ни localStorage, ни `t()`, ни
// сети. Готовые подписи, готовые строки записей и обработчики приезжают
// параметрами; перевод, форматирование дат и сетевые клиенты остаются у
// library-view.ts. Причина не стилистическая: чистый модуль проверяется своими
// подписями-маркерами, то есть тест ловит ПОВЕДЕНИЕ, а не текущее содержимое
// словаря.
//
// ── Четыре вещи, которые обязаны быть именно так ───────────────────────────
//
// 1. РАЗДЕЛА НЕТ У ПЕСНИ БЕЗ ЗАПИСЕЙ. Не пустой раздел с надписью «записей
//    нет», а отсутствующий — решает вызывающий, не строя его вовсе (прототип
//    делает то же самое). Здесь этого гейта намеренно нет: второе место,
//    решающее тот же вопрос, разошлось бы с первым молча.
//
// 2. РАДИО ИМЕЕТ СМЫСЛ ТОЛЬКО ПРИ ДВУХ И БОЛЕЕ ЗАПИСЯХ. У песни с одной
//    выбирать не из чего, а орган выбора из одного пункта обещает выбор,
//    которого нет, — та же причина, по которой выбор команды в шапке
//    библиотеки появляется только со второй команды. Метка «главная» при одной
//    записи тоже не рисуется: помечать нечего.
//
// 3. СМЕНА ГЛАВНОЙ НЕ ВРЁТ ПРО СОСТОЯНИЕ. Известное состояние (`defaultId`)
//    меняется ТОЛЬКО после успешного ответа, и раздел перерисовывается там же,
//    одной строкой. Отказ — прежний выбор на месте и причина словом. Отдельно
//    названа `revoked` (410): запись успели отозвать в другом окне, и пометить
//    её главной значит направить каждого читателя песни в пустоту. Человек
//    обязан прочитать это словами, а не кодом ответа.
//
// 4. ВИДИМОСТЬ ЗАПИСИ ЕДЕТ ПО СУЩЕСТВУЮЩЕМУ ПУТИ. Тот же `setVisibility`, что
//    у панели читателей (shares-api.ts) — раздел даёт ему второй ВХОД, а не
//    второй путь. Текущее положение ЧИТАЕТСЯ с сервера (`loadVisibility`), а
//    не угадывается: орган без прочитанного состояния остаётся невыбранным,
//    как и в панели читателей, — это честнее, чем нарисовать «команда» и
//    оказаться неправым.
//
// Каждый орган гаснет вместе со своей проводкой: без `setDefault` нет радио,
// без `setVisibility` нет переключателя, без `removeRecording` нет снятия.
// Орган, который ничего не делает, хуже отсутствующего — правило проекта, и
// условие в DOM делает пропавшую проводку видимой тесту, а не только человеку
// в бою.

// Сегментированный орган DS — тот же, что у видимости слов и у вкладок
// библиотеки. Импорт регистрирует кастомный элемент, его light-DOM CSS едет
// рядом (без второго импорта орган рисуется голым).
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-segmented-control.css';

/**
 * Лестница отказов маршрута `PATCH /api/shares/:id/default`, каждая ступень
 * своим словом: 401 → `auth`, 403 `not_owner` → `forbidden`, 404 → `gone`,
 * 410 `revoked` → `revoked`, 409 `share_without_song` → `noSong`, всё
 * остальное → `network`. Схлопнуть их в одно «не удалось» значит не сказать
 * человеку ничего: «войдите заново», «это не ваша запись», «её уже нет», «её
 * отозвали» и «сервер не ответил» требуют разных действий.
 */
export type SongSheetRecordingFail = 'auth' | 'forbidden' | 'gone' | 'revoked' | 'noSong' | 'network';

export type SongSheetRecordingResult = { ok: true } | { ok: false; reason: SongSheetRecordingFail };

export interface SongSheetRecording {
  shareId: string;
  /** Имя записи — готовая строка (у нас это дата публикации). */
  name: string;
  /**
   * Метастрока под именем: «12 tracks · added 2 days ago» у прототипа.
   * Собирает ВЫЗЫВАЮЩИЙ, и `null` — обычное состояние, а не угловой случай:
   * число дорожек `/api/library` сегодня не отдаёт вовсе, поэтому мета несёт
   * то, что есть (возраст записи), и пропадает, когда нечего нести.
   */
  meta: string | null;
  /** Помечена главной — с неё начинается песня для каждого читателя. */
  isDefault: boolean;
}

export interface SongSheetRecordingsLabels {
  /** Надзаголовок секции — «Recordings». */
  heading: string;
  /** Доступное имя группы радио — «Which recording opens first» прототипа.
   *  Стоит на группе, а не в надзаголовке: раздел делает больше, чем выбор
   *  главной, и заявлять одно вместо трёх было бы неправдой. */
  defaultGroupLabel: string;
  /** Метка выбранной записи — «Default». */
  defaultBadge: string;
  /** Доступное имя переключателя видимости и подписи его положений. */
  visLabel: string;
  visTeam: string;
  visPublic: string;
  /** Видимость не сохранилась. Путь общий с панелью читателей, и итог там —
   *  булев `{ ok }`, то есть назвать причину точнее нечем. */
  visError: string;
  /** Текущая видимость ещё ЧИТАЕТСЯ. Обязана существовать: без неё оба
   *  сегмента секунду стоят серыми, и на экране это читается как «выбор
   *  пуст», а не «идёт чтение», — поверхность молча выглядит сломанной. */
  visReading: string;
  /** Видимость прочитать НЕ УДАЛОСЬ (ответа нет, или он не про эту запись).
   *  Орган остаётся невыбранным — как и был, — но теперь сказано почему:
   *  иначе то же серое «непонятное состояние» просто переезжает с секунды
   *  ожидания на всё время жизни листа. */
  visUnknown: string;
  /** Снятие записи с публикации и его подтверждение. */
  removeBtn: string;
  /** Текст подтверждения. ОБЯЗАН называть последствие: ссылка на эту запись
   *  перестанет играть у всех, кому её отправили. */
  removeWarning: string;
  removeConfirm: string;
  removeCancel: string;
  /** Причины отказа — по ступеням лестницы выше, каждая своим словом. */
  errAuth: string;
  errForbidden: string;
  errGone: string;
  errRevoked: string;
  errNoSong: string;
  errNetwork: string;
}

export interface SongSheetRecordingsOpts {
  /** Записи ЭТОЙ песни, принадлежащие смотрящему. Порядок — как отдал
   *  вызывающий (у нас: главная первой, остальные новыми вперёд). */
  recordings: SongSheetRecording[];
  labels: SongSheetRecordingsLabels;
  /** `PATCH /api/shares/:id/default`. Без неё радио не рисуется. */
  setDefault?: (shareId: string) => Promise<SongSheetRecordingResult>;
  /** Текущая видимость записи — читается, а не угадывается. `null` (и отказ)
   *  оставляет орган без выбранного положения, как в панели читателей. */
  loadVisibility?: (shareId: string) => Promise<'team' | 'public' | null>;
  /** СУЩЕСТВУЮЩИЙ путь смены видимости (shares-api.setVisibility). Без неё
   *  переключатель не рисуется. */
  setVisibility?: (shareId: string, next: 'team' | 'public') => Promise<{ ok: boolean }>;
  /**
   * Главной стала ДРУГАЯ запись — вызывающему пора перерисовать библиотеку.
   *
   * Зовётся ТОЛЬКО после успеха, как `onRemoved` ниже: пометка действует не
   * внутри раздела, а везде — тап по слоту аудио в самой библиотеке открывает
   * голову списка версий, а голову переставляет ответ `/api/library`. Без
   * этого владелец помечал главной B, закрывал лист, тапал слот той же строки
   * — и открывалась A.
   *
   * Лист при этом НЕ закрывается (в отличие от снятия записи или слов):
   * предмет управления никуда не делся, и закрывать лист за человека, который
   * всего лишь поставил точку в радио, значило бы прыгнуть вместо него.
   */
  onDefaultChanged?: (shareId: string) => void;
  /** Снять запись с публикации. Без неё действия нет вовсе. */
  removeRecording?: (shareId: string) => Promise<SongSheetRecordingResult>;
  /** Запись снята — вызывающему пора перерисовать библиотеку и лист. */
  onRemoved?: (shareId: string) => void;
}

/** Сегментированный орган DS: значение читается и пишется свойством. */
type SegmentedEl = HTMLElement & {
  segments: Array<{ value: string; label: string }>;
  value: string;
};

export function buildSongSheetRecordings(opts: SongSheetRecordingsOpts): HTMLElement {
  const { labels: L } = opts;

  const sec = document.createElement('section');
  sec.dataset.role = 'sheet-recordings';
  sec.className = 'lib-sheet-sec';

  const head = document.createElement('h3');
  head.dataset.role = 'sheet-recordings-head';
  head.className = 'lib-sheet-sec-head';
  head.textContent = L.heading;
  sec.appendChild(head);

  const listEl = document.createElement('div');
  listEl.dataset.role = 'sheet-recordings-list';
  listEl.className = 'lib-sheet-vers';
  sec.appendChild(listEl);

  // Строка состояния — ОДНА на весь раздел и ЗА пределами перерисовываемого
  // списка: причина отказа обязана пережить перерисовку, иначе человек увидит
  // возврат выбора без объяснения, что и есть «врать про состояние».
  const status = document.createElement('p');
  status.dataset.role = 'recordings-status';
  status.className = 'lib-sheet-sec-status';
  status.setAttribute('aria-live', 'polite');
  sec.appendChild(status);

  /** Выбирать есть из чего только со второй записи (пункт 2 в шапке). */
  const pickable = opts.recordings.length > 1 && Boolean(opts.setDefault);

  /** Известное состояние. Меняется ТОЛЬКО после успешного ответа (пункт 3). */
  let defaultId: string | null = opts.recordings.find((r) => r.isDefault)?.shareId ?? null;
  /** У какой записи открыто подтверждение снятия. Живёт снаружи paint(). */
  let confirmingId: string | null = null;

  /**
   * Прочитанная видимость записи. Кэш — потому что список перерисовывается
   * (смена главной, открытие подтверждения), а второй запрос за тем же ответом
   * означал бы мигающий орган и лишний поход в сеть.
   */
  const visKnown = new Map<string, 'team' | 'public'>();
  /**
   * Чтение видимости В ПУТИ — ПРОМИС, а не булев признак «уже спрашивали».
   * Разница видна ровно там, где перерисовка (смена главной, открытие
   * подтверждения) случается ДО ответа: признак говорил новому органу «не
   * спрашивай», а ответ уходил в старый, уже отсоединённый от документа, — и
   * орган оставался без значения навсегда. Промис новый орган переподписывает
   * на тот же ответ, сохраняя «один запрос на запись».
   */
  const visRead = new Map<string, Promise<'team' | 'public' | null>>();

  const reasonText = (reason: SongSheetRecordingFail): string => {
    switch (reason) {
      case 'auth': return L.errAuth;
      case 'forbidden': return L.errForbidden;
      case 'gone': return L.errGone;
      case 'revoked': return L.errRevoked;
      case 'noSong': return L.errNoSong;
      default: return L.errNetwork;
    }
  };

  /**
   * Перерисовать И ПОДОБРАТЬ ФОКУС в строке, откуда пришло нажатие. `paint()`
   * делает `innerHTML = ''`, то есть уничтожает нажатую кнопку, и фокус падает
   * на <body>. Мышью этого не видно; с клавиатуры человек, дошедший табом до
   * «Убрать» и нажавший Enter, получает отрисованное подтверждение, до
   * которого не дотянуться иначе как новым проходом Tab с начала документа.
   *
   * Цель называют вызывающий и СТРОКА: записей в разделе несколько, и фокус,
   * уехавший в подтверждение соседней, был бы не лучше <body>.
   */
  function paintFocusing(shareId: string, role: string): void {
    paint();
    listEl
      .querySelector(`[data-role="sheet-rec-row"][data-share-id="${shareId}"]`)
      ?.querySelector<HTMLElement>(`[data-role="${role}"]`)
      ?.focus();
  }

  function paint(): void {
    listEl.innerHTML = '';
    if (pickable) {
      listEl.setAttribute('role', 'radiogroup');
      listEl.setAttribute('aria-label', L.defaultGroupLabel);
    }
    for (const r of opts.recordings) listEl.appendChild(buildRow(r));
  }

  function buildRow(r: SongSheetRecording): HTMLElement {
    const row = document.createElement('div');
    row.dataset.role = 'sheet-rec-row';
    row.dataset.shareId = r.shareId;
    row.className = 'lib-sheet-ver';

    const main = document.createElement('span');
    main.className = 'lib-sheet-ver-main';
    const nameEl = document.createElement('span');
    nameEl.className = 'lib-sheet-ver-name';
    nameEl.textContent = r.name;
    main.appendChild(nameEl);
    if (r.meta) {
      const metaEl = document.createElement('span');
      metaEl.className = 'lib-sheet-ver-meta';
      metaEl.textContent = r.meta;
      main.appendChild(metaEl);
    }

    if (pickable) {
      // Кружок выбора и имя — ОДНА тач-цель (прототип держит всю строку
      // кнопкой): на телефоне целиться в 20-пиксельный кружок нечем.
      // Органы видимости и снятия стоят СНАРУЖИ неё — кнопка в кнопке
      // невалидна, и вложенная не нажимается предсказуемо ни мышью, ни
      // клавиатурой.
      const pick = document.createElement('button');
      pick.type = 'button';
      pick.dataset.role = 'sheet-rec-default';
      pick.className = 'lib-sheet-ver-pick';
      pick.setAttribute('role', 'radio');
      const checked = defaultId === r.shareId;
      pick.setAttribute('aria-checked', String(checked));

      const dot = document.createElement('span');
      dot.className = 'lib-sheet-ver-radio';
      dot.setAttribute('aria-hidden', 'true');
      pick.appendChild(dot);
      pick.appendChild(main);

      if (checked) {
        const badge = document.createElement('span');
        badge.dataset.role = 'sheet-rec-default-badge';
        badge.className = 'lib-sheet-ver-default';
        badge.textContent = L.defaultBadge;
        pick.appendChild(badge);
      }

      pick.addEventListener('click', () => onPick(r.shareId));
      row.appendChild(pick);
    } else {
      const plain = document.createElement('div');
      plain.className = 'lib-sheet-ver-plain';
      plain.appendChild(main);
      row.appendChild(plain);
    }

    row.appendChild(confirmingId === r.shareId ? buildConfirm(r.shareId) : buildActions(r.shareId));
    return row;
  }

  function onPick(shareId: string): void {
    const setDefault = opts.setDefault;
    // Уже главная — звать нечего: маршрут сделал бы ту же работу заново, а
    // человек увидел бы вспышку состояния без единого изменения.
    if (!setDefault || shareId === defaultId) return;
    status.textContent = '';
    Promise.resolve(setDefault(shareId)).then(
      (res) => {
        if (res.ok) { defaultId = shareId; paint(); opts.onDefaultChanged?.(shareId); return; }
        // Прежний выбор ОСТАЁТСЯ нарисованным, причина называется словом
        // (пункт 3). Перерисовать под новую запись после отказа — ровно то
        // враньё, против которого этот путь и написан.
        status.textContent = reasonText(res.reason);
      },
      () => { status.textContent = L.errNetwork; },
    );
  }

  function buildActions(shareId: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'lib-sheet-ver-actions';

    if (opts.setVisibility) wrap.appendChild(buildVis(shareId));

    if (opts.removeRecording) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.role = 'sheet-rec-remove';
      btn.className = 'lib-sheet-ver-remove';
      btn.textContent = L.removeBtn;
      btn.addEventListener('click', () => {
        confirmingId = shareId;
        paintFocusing(shareId, 'sheet-rec-remove-no');
      });
      wrap.appendChild(btn);
    }

    return wrap;
  }

  /**
   * Переключатель видимости ПЛЮС строка его состояния, сиблингами в ряду
   * действий (фрагмент, а не ещё одна обёртка: ряд уже flex с зазором, и
   * второй контейнер добавил бы правило раскладки на пустом месте).
   *
   * Строка состояния и есть часть C задачи. Орган DS без выбранного положения
   * рисует ОБА сегмента серыми, и секунду до ответа `listMyShares` это
   * читается как «выбор пуст», а не «состояние ещё читается». Названное
   * состояние закрывает и второй случай — ответ пришёл, но сказать по нему
   * нечего: там серое «непонятное состояние» иначе оставалось бы навсегда.
   */
  function buildVis(shareId: string): DocumentFragment {
    const frag = document.createDocumentFragment();
    const seg = document.createElement('bw-segmented-control') as SegmentedEl;
    seg.dataset.role = 'sheet-rec-vis';
    seg.setAttribute('size', 'sm');
    seg.setAttribute('aria-label', L.visLabel);
    seg.segments = [
      { value: 'team', label: L.visTeam },
      { value: 'public', label: L.visPublic },
    ];

    const state = document.createElement('span');
    state.dataset.role = 'sheet-rec-vis-state';
    state.className = 'lib-sheet-ver-vis-state';
    state.setAttribute('aria-live', 'polite');

    const known = visKnown.get(shareId);
    if (known) {
      seg.value = known;
    } else if (opts.loadVisibility) {
      state.textContent = L.visReading;
      let pending = visRead.get(shareId);
      if (!pending) {
        // Отказ сводится к «нет ответа» ЗДЕСЬ, а не в каждом подписчике:
        // разницы между «сервер промолчал» и «ответил не про эту запись» для
        // органа нет — в обоих случаях выбирать нечего.
        pending = Promise.resolve(opts.loadVisibility(shareId)).catch(() => null);
        visRead.set(shareId, pending);
      }
      void pending.then((vis) => {
        if (vis !== 'team' && vis !== 'public') { state.textContent = L.visUnknown; return; }
        visKnown.set(shareId, vis);
        // Прямо в живой орган, а не через перерисовку: перерисовка уронила
        // бы фокус и открытое подтверждение соседней строки.
        seg.value = vis;
        // Сказало своё и ушло: дальше правду говорит сам орган, а
        // оставленная подпись спорила бы с ним.
        state.textContent = '';
      });
    } else {
      // Менять видимость есть чем, а читать её нечем — состояние неизвестно и
      // не станет известным. Честнее сказать это словом, чем нарисовать серое.
      state.textContent = L.visUnknown;
    }

    seg.addEventListener('bw-segmented-change', (e) => {
      const setVisibility = opts.setVisibility;
      if (!setVisibility) return;
      const next = (e as CustomEvent<{ value: string }>).detail?.value === 'public' ? 'public' : 'team';
      const prev = visKnown.get(shareId);
      if (next === prev) return;
      status.textContent = '';
      Promise.resolve(setVisibility(shareId, next)).then(
        (res) => {
          if (res.ok) { visKnown.set(shareId, next); return; }
          // Возврат В ПРЕЖНЕЕ положение: орган DS уже перекинулся сам, и
          // оставить его так значило бы показать несохранённое сохранённым.
          seg.value = prev ?? '';
          status.textContent = L.visError;
        },
        () => { seg.value = prev ?? ''; status.textContent = L.visError; },
      );
    });

    frag.appendChild(seg);
    frag.appendChild(state);
    return frag;
  }

  /**
   * Подтверждение НА МЕСТЕ действий этой строки, а не модалкой поверх листа:
   * лист сам — модальная поверхность, и вторая поверх неё забирает фокус у
   * первой. Текст ОБЯЗАН называть последствие: запись уйдёт из библиотеки, и
   * ссылка на неё перестанет играть — у всех.
   */
  function buildConfirm(shareId: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.dataset.role = 'sheet-rec-remove-confirm';
    wrap.className = 'lib-sheet-confirm';

    const text = document.createElement('p');
    text.className = 'lib-sheet-confirm-text';
    text.textContent = L.removeWarning;
    wrap.appendChild(text);

    const row = document.createElement('div');
    row.className = 'lib-sheet-confirm-row';

    const no = document.createElement('button');
    no.type = 'button';
    no.dataset.role = 'sheet-rec-remove-no';
    no.className = 'lib-sheet-confirm-no';
    no.textContent = L.removeCancel;
    no.addEventListener('click', () => { confirmingId = null; paintFocusing(shareId, 'sheet-rec-remove'); });
    row.appendChild(no);

    const yes = document.createElement('button');
    yes.type = 'button';
    yes.dataset.role = 'sheet-rec-remove-yes';
    yes.className = 'lib-sheet-confirm-yes';
    yes.textContent = L.removeConfirm;
    yes.addEventListener('click', () => {
      const removeRecording = opts.removeRecording;
      if (!removeRecording) return;
      yes.disabled = true;
      status.textContent = '';
      Promise.resolve(removeRecording(shareId)).then(
        (res) => {
          if (res.ok) { opts.onRemoved?.(shareId); return; }
          // Не получилось — строка остаётся, действие снова доступно, причина
          // названа. Тихо закрыть подтверждение значило бы показать снятие,
          // которого не было.
          confirmingId = null;
          paint();
          status.textContent = reasonText(res.reason);
        },
        () => { confirmingId = null; paint(); status.textContent = L.errNetwork; },
      );
    });
    row.appendChild(yes);

    wrap.appendChild(row);
    return wrap;
  }

  paint();
  return sec;
}
